// React & third-party libraries
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  IconButton,
  MenuItem,
} from "@mui/material";

// Components
import Spinner from "../spinner/Spinner";

// Services
import { getStorageItemLocal } from "../../services/storageServices";

// Config
import { appConfig } from "../../config/appConfig";

// Assets
import arrowback from "../../assets/arrow_back.svg";

// Styles
import "./PublishStatePDFFormDialog.scss";

type AdminEntry = {
  [x: string]: any;
  id: number;
  state: string;
  pdFname: string;
  pdfUrls: string | string[];
  isActive: boolean;
  status: string;
  stateName: string;
};
interface State {
  id: number;
  name: string;
}

interface PublishStatePDFFormDialogProps {
  open: boolean;
  onClose?: () => void;

  fetchData: () => void;
  resetForm: () => void;

  formData: AdminEntry;
  setFormData: React.Dispatch<React.SetStateAction<AdminEntry>>;
  editingIndex: any;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingIndex: React.Dispatch<React.SetStateAction<any>>;
  states: any;
  dataList?: any;
}

const PublishStatePDFFormDialog: React.FC<PublishStatePDFFormDialogProps> = ({
  open,
  onClose,
  fetchData,
  resetForm,
  formData,
  setFormData,
  editingIndex,
  setShowForm,
  setEditingIndex,
  states,
  dataList,
}) => {
  // PDF Upload State
  const [selectedPdf, setSelectedPdf] = useState<File[]>([]);
  const [fileName, setfileName] = useState("No PDF");
  const [isActive, setIsActive] = useState(false);
  const [pdferror, setPdfError] = useState<string | null>(null);

  // Loading State
  const [loading, setLoading] = useState(false);

  // Constants
  const MAX_FILE_SIZE_MB1 = 15;
  const ALLOWED_TYPE = "application/pdf";

  useEffect(() => {
    setIsActive(formData.isActive);

    let temp: string[] = [];

    if (Array.isArray(formData.pdfUrls)) {
      temp = formData.pdfUrls.map((img) => img?.trim?.()).filter(Boolean);
    } else if (typeof formData.pdfUrls === "string") {
      temp = formData.pdfUrls
        .split(",")
        .map((img) => img.trim())
        .filter(Boolean);
    }

    if (temp.length > 0) {
      const extractedFileName = temp[0].split("/").pop()?.trim() ?? "";

      setfileName(extractedFileName);
    } else {
      setfileName(""); // clear filename fallback
    }
  }, [formData]);

  const handleCancel = () => {
    onClose?.();
    setShowForm(false);
    setEditingIndex(null);
    resetForm();
    setShowForm(false);
    setPdfError(null);
    setIsActive(false);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      //   [e.target.name]: e.target.value,
      pdFname: e.target.value,
      lastUpdated: new Date().toISOString(),
    });
  };
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "application/pdf"
  ) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      if (type === "application/pdf") {
        setSelectedPdf(filesArray);
      }
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    if (files.length === 0) {
      setPdfError("Please select a PDF file.");
      return;
    }

    const file = files[0]; // Assuming single file upload for PDF

    if (file.type !== ALLOWED_TYPE) {
      setPdfError("Only .pdf files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB1 * 1024 * 1024) {
      setPdfError("File size must be less than or equal to 10MB.");
      return;
    }

    setPdfError(null);
    handleFileChange(e, "application/pdf"); //file.name
    setfileName(file.name);
  };

  const handleSave = async () => {
    if (!formData.state) {
      alert("state is required!");
      return;
    }
    if (!formData.pdFname) {
      alert("PDF Name is required!");
      return;
    }
    const currentStateEntries = dataList.filter(
      (entry: any) =>
        entry.state === formData.state &&
        (editingIndex === null || entry.id !== formData.id)
    );

    if (currentStateEntries.length >= 3) {
      alert("Cannot add more than 3 entries for the selected state.");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("id", editingIndex?.toString() ?? "0");
    formDataToSend.append("state", formData.state);
    formDataToSend.append("pdFname", formData.pdFname);
    formDataToSend.append("isActive", isActive.toString());

    formDataToSend.append("status", "Approved"); //Approved  Pending for Approval

    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("uploadBy", username);
    // Append selectedImages

    selectedPdf.forEach((pdf) => {
      formDataToSend.append("pdfUrls", pdf);
    });

    setLoading(true);

    const url =
      editingIndex !== null
        ? `${appConfig.serviceUrls.saveStatePDFEntries}/${formData.id}`
        : `${appConfig.serviceUrls.saveStatePDFEntries}/upload`;
    try {
      const response = await fetch(url, {
        method: editingIndex !== null ? "PUT" : "POST",
        body: formDataToSend,
      });
      if (!response.ok) throw new Error("Failed to save data");
      alert("Data saved successfully");
      fetchData();
      resetForm();
      (document.getElementById("pdfUpload") as HTMLInputElement).value = "";
      setSelectedPdf([]);
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error saving data:", error);
      // alert(`Failed to save data: ${error}`);
    } finally {
      setLoading(false);
      setShowForm(false);
    }
  };
  const handleIsActive = () => {
    setIsActive(!isActive);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "900px",
          height: "450px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#F7F7F7",
          padding: "20px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={handleCancel}
            className="custom-icon-button"
          >
            <img src={arrowback} alt="visible" className="custom-icon" />
          </IconButton>
          <Typography className="custom-icon-text">
            {editingIndex !== null
              ? "Edit State Level PDF"
              : "Add State Level PDF"}
          </Typography>
        </Box>

        <Box></Box>
      </Box>

      <Box sx={{ marginLeft: "20px" }}>
        <Typography className="text-style">State</Typography>
        <Box className="pdf-name-wrapper">
          <TextField
            select
            placeholder="Select State"
            size="small"
            name="state"
            value={formData.state}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value })
            }
            variant="standard"
            fullWidth
            InputProps={{ disableUnderline: true }}
          >
            {states.map((st: State) => (
              <MenuItem key={st.id} value={st.name}>
                {st.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
      {/* 2nd */}
      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            background: "#FFF",
            padding: "50px 16px",
          }}
        >
          <Box>
            <Typography className="text-style">PDF Name</Typography>
            <Box className="pdf-name-wrapper">
              <TextField
                placeholder="Enter PDF Name"
                size="small"
                name="pdFname" // match the key
                value={formData.pdFname} // match the key
                onChange={
                  (e) => setFormData({ ...formData, pdFname: e.target.value }) // same key
                }
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
              />
            </Box>
          </Box>
          <Box>
            <Typography className="text-style">Add PDF File</Typography>
            <Box className="pdf-upload-row">
              <Box className="pdf-name-wrapper">
                <TextField
                  placeholder="Select PDF"
                  size="small"
                  name="pdfName"
                  value={fileName}
                  onChange={handleChange}
                  variant="standard"
                  fullWidth
                  disabled={true}
                  InputProps={{ disableUnderline: true }}
                />
              </Box>

              <Box>
                <Button component="label" className="choose-pdf-button">
                  <Typography className="pdf-button-text ">Browse</Typography>
                  <input
                    id="pdfUpload"
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={handlePdfUpload}
                  />
                </Button>
                {pdferror && <Alert severity="error">{pdferror}</Alert>}
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography className="text-style">Active/Disable</Typography>

            <Box
              onClick={handleIsActive}
              sx={{ padding: "15px" }}
              className="status-wrapper"
            >
              <Box
                className={`status-toggle1 ${isActive ? "active" : "Disable"}`}
              >
                <Box className="status-circle" />
              </Box>

              <Typography
                sx={{ width: "30px" }}
                className="active-txt"
                component="span"
              >
                {isActive ? "Active" : "Disable"}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <DialogActions>
        <Button
          onClick={handleCancel}
          className="cancel-button"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={pdferror != null}
          onClick={handleSave}
          className="submit-button"
        >
          {editingIndex !== null ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishStatePDFFormDialog;
